// utils/datCommon.js
//
// Generic helpers for BIR alphalist DAT files, shared by the SAWT builder
// (utils/sawtDat.js) and — once its layout is confirmed — the QAP builder
// (docs/bir-references-qap/QAP_LAYOUT.md). Extracted from sawtDat.js with no
// behaviour change; the SAWT fixture (utils/__fixtures__/12345678900001220251702.DAT)
// is the byte-level check.
//
// Conventions confirmed by that BIR-accepted SAWT sample (and only by it — a
// QAP sample has not been seen yet):
//   • ASCII, comma-delimited, CRLF line endings AND a trailing CRLF
//   • amounts as plain decimals with exactly two places, no thousands separator
//   • return periods MM/YYYY inside records; MMYYYY in file names
//
// Record tags, field order and quoting are per-file-type and stay in each
// builder.

export { cleanTIN } from './tinFormat';

export const CRLF = '\r\n';

export const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Uppercase, collapse whitespace, and drop the two characters that would break
 * a quoted CSV field (comma, double quote). Everything else — accents, ñ,
 * other punctuation — passes through unchanged (uppercased where it has a case).
 */
export const cleanDatName = (name) =>
  String(name ?? '').toUpperCase().replace(/[,"]/g, '').replace(/\s+/g, ' ').trim();

/**
 * Strip diacritics through Unicode NFD: Ñ → N, É → E, ü → u. Nothing else
 * changes. Not used by the SAWT builder (its names pass ñ through — SAWT
 * output is unchanged); a layout opts in.
 */
export const stripDiacritics = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Positional split of a single stored name: first token = last name, final
 * token = middle name, everything between = first name. Compound surnames
 * ("DELA CRUZ") are a known open item — the stored name has no boundary to use.
 */
export function splitIndividualName(fullName) {
  const parts = cleanDatName(fullName).split(' ').filter(Boolean);
  if (parts.length === 0) return { last: '', first: '', middle: '' };
  if (parts.length === 1) return { last: parts[0], first: '', middle: '' };
  if (parts.length === 2) return { last: parts[0], first: parts[1], middle: '' };
  return { last: parts[0], first: parts.slice(1, -1).join(' '), middle: parts[parts.length - 1] };
}

/**
 * Pesos → integer centavos (half away from zero via Math.round on the scaled
 * value), or null when not a finite number. Money in a DAT is summed in
 * centavos so control totals cannot drift from the two-decimal detail values.
 */
export const toCentavos = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

/** Integer centavos → "1234.50" (two decimals, no separators; "-" for negatives). */
export const formatCentavos = (c) => {
  const sign = c < 0 ? '-' : '';
  const abs = Math.abs(c);
  return `${sign}${Math.floor(abs / 100)}.${pad2(abs % 100)}`;
};

/** A percent rate as the DAT writes it: 2 → "2.00" (never rate / 100). */
export const formatRatePercent = (ratePercent) => Number(ratePercent).toFixed(2);

/** { month: 1–12, year: 'YYYY' } */
export const validPeriod = (p) =>
  Number.isInteger(Number(p?.month)) && Number(p.month) >= 1 && Number(p.month) <= 12
  && /^\d{4}$/.test(String(p?.year ?? ''));

/** In-record period: MM/YYYY. */
export const formatPeriod = (p) => `${pad2(Number(p.month))}/${p.year}`;

/** File-name period: MMYYYY. */
export const formatFilenamePeriod = (p) => `${pad2(Number(p.month))}${p.year}`;

/** Records → file content: CRLF between lines and a trailing CRLF. */
export const joinDatLines = (lines) => lines.join(CRLF) + CRLF;

/** Split file content back into records (ignores the trailing CRLF). */
export const splitDatLines = (content) => String(content).split(CRLF).filter(Boolean);

/** Trigger the browser download for a built file. */
export function downloadDat({ content, filename }) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
